import React, { useState, useEffect } from 'react';
import { generateDetailedReport } from './generateDetailedReport';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import TabNav from './TabNav';
import PlayerHeader from './PlayerHeader';
import DimensionScores from './DimensionScores';
import EvaluationBreakdown from './EvaluationBreakdown';
import StrengthsPanel from './StrengthsPanel';
import ImprovementPanel from './ImprovementPanel';
import RiskFactors from './RiskFactors';
import GradingTab from './GradingTab';
import PlayerProfile from './PlayerProfile';
import GameStatsTab from './GameStatsTab';
import RecruitingTab from './RecruitingTab';
import MeasurablesScore from './MeasurablesScore';
import { authFetch, API } from "../../api";

const FitAnalysis = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`?tab=${tab}`, { replace: true });
  };

  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'fit';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [player, setPlayer] = useState(null);
  const [fitScore, setFitScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayerData();
  }, [id]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlTab = queryParams.get('tab') || 'fit';
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (activeTab === 'fit' && player) {
      const refetchFitScore = async () => {
        try {
          const fitRes = await authFetch(`${API}/api/players/${id}/fit-score`);
          if (fitRes.ok) {
            const fitData = await fitRes.json();
            setFitScore(fitData);
          }
        } catch (error) {
          console.error('Error refetching fit score:', error);
        }
      };
      refetchFitScore();
    }
  }, [activeTab]);

  const fetchPlayerData = async () => {
    setLoading(true);
    try {
      const playerRes = await authFetch(`${API}/api/players/${id}`);
      const playerData = await playerRes.json();
      setPlayer(playerData);

      const fitRes = await authFetch(`${API}/api/players/${id}/fit-score`);
      if (fitRes.ok) {
        const fitData = await fitRes.json();
        setFitScore(fitData);
      } else {
        setFitScore({
          overall: 0,
          tier: 'NOT GRADED',
          highlights: ['No grades yet'],
          dimensionScores: {
            schemeFit: 0, skillFit: 0, physicalFit: 0, mentalFit: 0, contextFit: 0
          },
          strengths: [],
          improvements: [],
          risks: {
            injuryConcern: 0, projectionRisk: 0, hasInjuryHistory: false, hasDevelopmentUncertainty: false
          }
        });
      }
    } catch (error) {
      console.error('Error fetching player data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (photoUrl) => {
    setPlayer(prev => ({ ...prev, photo_url: photoUrl }));
  };

  const handlePlayerUpdate = (updatedPlayer) => {
    setPlayer(updatedPlayer);
  };

  const [reportLoading, setReportLoading] = useState(false);
  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      await generateDetailedReport(player, fitScore);
    } catch (e) {
      console.error('Report generation failed:', e);
      alert('Failed to generate report.');
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-[#00D4FF] text-lg">Loading player data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-[#0A0E27] p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="panel-card text-center py-12">
            <h2 className="text-white text-2xl font-bold mb-4">Player Not Found</h2>
            <p className="text-[#B0BEC5]">Could not load player data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="hidden md:flex justify-between items-center mb-6">
          <div className="text-3xl">
            <span className="font-bold text-white">Scheme</span><span className="font-bold text-[#00D4FF]">Sync</span>
          </div>
        </div>

        <TabNav activeTab={activeTab} onTabChange={handleTabChange} />

        {activeTab === 'profile' && (
          <PlayerProfile player={player} onPhotoUpload={handlePhotoUpload} onPlayerUpdate={handlePlayerUpdate} />
        )}

        {activeTab === 'fit' && (
          <>
            <PlayerHeader player={player} fitScore={fitScore} />

            {/* Measurables Score */}
            <MeasurablesScore playerId={player.id} />
            {fitScore && fitScore.overall > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                  <DimensionScores scores={fitScore.dimensionScores} />
                  <EvaluationBreakdown risks={fitScore.risks} dimensionScores={fitScore.dimensionScores} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
                  <StrengthsPanel strengths={fitScore.strengths} />
                  <ImprovementPanel improvements={fitScore.improvements} />
                  <RiskFactors confidenceScore={fitScore.confidence_score} />
                </div>
                <div className="flex justify-center gap-4 pb-2">
                  <button onClick={handleGenerateReport} disabled={reportLoading} className="btn-secondary">
                    {reportLoading ? 'Generating Report...' : 'Download Detailed Report'}
                  </button>
                </div>
              </>
            ) : (
              <div className="panel-card text-center py-12">
                <h3 className="text-white text-xl font-bold mb-2">No Fit Score Available</h3>
                <p className="text-[#B0BEC5] mb-4">This player hasn't been graded yet. Go to the Grading tab to start evaluating.</p>
                <button onClick={() => handleTabChange('grading')} className="btn-primary">Start Grading</button>
              </div>
            )}
          </>
        )}

        {activeTab === 'grading' && (
          <GradingTab playerId={player.id} playerName={player.name} playerPosition={player.position} />
        )}

        {activeTab === 'game-stats' && (
          <GameStatsTab player={player} />
        )}

        {activeTab === 'recruiting' && (
          <RecruitingTab player={player} onPlayerUpdate={fetchPlayerData} />
        )}
      </div>
    </div>
  );
};

export default FitAnalysis;
